import React, { useEffect, useMemo, useState } from "react";
import "./FoodRadarRewards.css";

const API_BASE = "/api";

const safeArray = (value) => (Array.isArray(value) ? value : []);

const safeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};


const renderRatingStars = (ratingValue) => {
  const rating = Math.max(0, Math.min(5, safeNumber(ratingValue, 0)));
  const filledStars = Math.round(rating);
  const emptyStars = 5 - filledStars;

  return `${"★".repeat(filledStars)}${"☆".repeat(emptyStars)}`;
};

const getVendorId = (vendor = {}) =>
  Number(
    vendor.vendorId ||
      vendor.vendor_id ||
      vendor.id ||
      0
  );

const getVendorName = (vendor = {}) =>
  vendor.name ||
  vendor.shop_name ||
  vendor.shopName ||
  "Food Shop";

const getVendorImageSrc = (vendor = {}) => {
  const rawValue =
    vendor.shopImageUrl ||
    vendor.shopLogoUrl ||
    vendor.shopImage ||
    vendor.shopLogo ||
    vendor.shop_image ||
    vendor.shop_logo ||
    vendor.image_url ||
    vendor.image ||
    "";

  if (!rawValue || typeof rawValue !== "string") return null;

  let imageValue = rawValue.trim();

  try {
    const parsed = JSON.parse(imageValue);

    if (Array.isArray(parsed) && parsed.length > 0) {
      imageValue = String(parsed[0] || "").trim();
    } else if (typeof parsed === "string") {
      imageValue = parsed.trim();
    } else if (parsed && typeof parsed === "object") {
      imageValue = String(
        parsed.url ||
          parsed.path ||
          parsed.image ||
          parsed.filename ||
          parsed.file ||
          ""
      ).trim();
    }
  } catch {
    // Normal image path.
  }

  if (!imageValue) return null;

  if (
    imageValue.startsWith("http://") ||
    imageValue.startsWith("https://") ||
    imageValue.startsWith("data:")
  ) {
    return imageValue;
  }

  if (imageValue.startsWith("/")) return imageValue;
  if (imageValue.startsWith("uploads/")) return `/${imageValue}`;

  return `/uploads/${imageValue}`;
};

const normalizeRewardVendor = (vendor = {}) => {
  const availableCoins = Math.max(
    0,
    parseInt(
      vendor.available_coins ??
        vendor.availableCoins ??
        vendor.customer_available_coins ??
        vendor.coins_available ??
        vendor.balance ??
        0,
      10
    ) || 0
  );

  const pendingCoins = Math.max(
    0,
    parseInt(
      vendor.pending_coins ??
        vendor.pendingCoins ??
        vendor.customer_pending_coins ??
        vendor.coins_pending ??
        0,
      10
    ) || 0
  );

  const averageRating = safeNumber(
    vendor.average_rating ??
      vendor.averageRating ??
      vendor.avg_rating ??
      vendor.rating ??
      vendor.vendor_rating ??
      0,
    0
  );

  const ratingCount = Math.max(
    0,
    parseInt(
      vendor.rating_count ??
        vendor.ratingCount ??
        vendor.total_ratings ??
        vendor.ratings_count ??
        vendor.review_count ??
        0,
      10
    ) || 0
  );

  return {
    ...vendor,
    vendorId: getVendorId(vendor),
    vendorName: getVendorName(vendor),
    availableCoins,
    pendingCoins,
    averageRating,
    ratingCount,
  };
};

function FoodRadarRewards() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [search, setSearch] = useState("");

  const customerId =
    localStorage.getItem("foodRadarCustomerId") || "";

  const customerMobile =
    localStorage.getItem("foodRadarCustomerMobile") || "";

  useEffect(() => {
    const loadRewards = async () => {
      if (!customerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMessage("");

        const params = new URLSearchParams({
          customerId,
          customer_id: customerId,
        });

        if (customerMobile) {
          params.set("mobile", customerMobile);
        }

        let loadedFromApi = false;

        try {
          const response = await fetch(
            `${API_BASE}/public/food-radar/rewards?${params.toString()}`
          );

          const data = await response.json();

          if (response.ok && data?.success) {
            const source =
              data.vendors ||
              data.rewards ||
              data.wallets ||
              data.data ||
              [];

            if (Array.isArray(source)) {
              const normalized = source
                .map(normalizeRewardVendor)
                .filter((item) => item.vendorId > 0);

              setVendors(normalized);
              sessionStorage.setItem(
                "foodRadarVendorRewards",
                JSON.stringify(normalized)
              );
              loadedFromApi = true;
            }
          }
        } catch (apiError) {
          console.warn(
            "Food Radar rewards API not available:",
            apiError
          );
        }

        if (!loadedFromApi) {
          const cached =
            JSON.parse(
              sessionStorage.getItem(
                "foodRadarVendorRewards"
              ) ||
                sessionStorage.getItem(
                  "foodRadarVendorSummaries"
                ) ||
                "[]"
            ) || [];

          setVendors(
            safeArray(cached)
              .map(normalizeRewardVendor)
              .filter((item) => item.vendorId > 0)
          );
        }
      } catch (error) {
        console.error("Food Radar rewards load error:", error);
        setErrorMessage(
          "We couldn't load your rewards. Please try again."
        );
        setVendors([]);
      } finally {
        setLoading(false);
      }
    };

    loadRewards();
  }, [customerId, customerMobile]);

  const totalAvailable = useMemo(
    () =>
      vendors.reduce(
        (sum, vendor) =>
          sum + safeNumber(vendor.availableCoins, 0),
        0
      ),
    [vendors]
  );

  const totalPending = useMemo(
    () =>
      vendors.reduce(
        (sum, vendor) =>
          sum + safeNumber(vendor.pendingCoins, 0),
        0
      ),
    [vendors]
  );

  const totalCoins = totalAvailable + totalPending;

  const filteredVendors = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) return vendors;

    return vendors.filter((vendor) =>
      `${vendor.vendorName} ${vendor.category || ""}`
        .toLowerCase()
        .includes(value)
    );
  }, [vendors, search]);

  const openVendor = (vendor) => {
    window.location.href = `/food-radar/order/${vendor.vendorId}`;
  };

  const useCoins = (vendor) => {
    if (vendor.availableCoins <= 0) return;

    sessionStorage.setItem(
      "foodRadarRedeemVendor",
      String(vendor.vendorId)
    );

    sessionStorage.setItem(
      "foodRadarRedeemAvailableCoins",
      String(vendor.availableCoins)
    );

    window.location.href =
      `/food-radar/order/${vendor.vendorId}?useCoins=1`;
  };

  if (!customerId) {
    return (
      <div className="frr-page">
        <header className="frr-header">
          <button
            type="button"
            onClick={() =>
              (window.location.href = "/food-radar")
            }
            aria-label="Back"
          >
            ‹
          </button>

          <div>
            <p>FOOD RADAR</p>
            <h1>My Rewards</h1>
          </div>

          <span />
        </header>

        <main className="frr-login-empty">
          <div className="frr-big-coin">🪙</div>
          <h2>Your rewards are waiting</h2>
          <p>
            Sign in to view your available and pending coins for each shop.
          </p>

          <button
            type="button"
            onClick={() =>
              (window.location.href = "/food-radar")
            }
          >
            Go to Food Radar
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="frr-page">
      <header className="frr-header">
        <button
          type="button"
          onClick={() =>
            (window.location.href = "/food-radar")
          }
          aria-label="Back"
        >
          ‹
        </button>

        <div>
          <p>FOOD RADAR</p>
          <h1>My Rewards</h1>
        </div>

        <span />
      </header>

      <main className="frr-main">
        <section className="frr-hero">
          <div className="frr-hero-copy">
            <p>YOUR REWARD WALLET</p>
            <h2>{totalCoins} Shop Coins</h2>
            <span>
              Your vendor-wise rewards in one place
            </span>
          </div>

          <div className="frr-hero-coin">🪙</div>
        </section>

        <section className="frr-summary-grid">
          <article className="available">
            <div className="frr-summary-icon">✓</div>
            <small>Available</small>
            <strong>{totalAvailable}</strong>
            <p>Ready to use now</p>
          </article>

          <article className="pending">
            <div className="frr-summary-icon">⏳</div>
            <small>Pending</small>
            <strong>{totalPending}</strong>
            <p>Available after order completion</p>
          </article>
        </section>

        <section className="frr-info-card">
          <span>💡</span>
          <p>
            Coins are vendor-specific and can only be used at the shop where they were earned.
          </p>
        </section>

        <section className="frr-search">
          <span>⌕</span>
          <input
            type="text"
            placeholder="Search vendor..."
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
          />
        </section>

        <section className="frr-section-head">
          <div>
            <p>YOUR COINS</p>
            <h2>Rewards from vendors</h2>
          </div>

          <span>
            {filteredVendors.length} Shops
          </span>
        </section>

        {errorMessage ? (
          <div className="frr-error">
            {errorMessage}
          </div>
        ) : null}

        {loading ? (
          <div className="frr-loading">
            <div />
            <p>Loading rewards...</p>
          </div>
        ) : filteredVendors.length === 0 ? (
          <div className="frr-empty-card">
            <span>🪙</span>
            <h3>No rewards yet</h3>
            <p>
              Place an order through Food Radar to start earning Shop Coins.
            </p>

            <button
              type="button"
              onClick={() =>
                (window.location.href = "/food-radar")
              }
            >
              Explore Food
            </button>
          </div>
        ) : (
          <section className="frr-vendor-list">
            {filteredVendors.map((vendor) => {
              const imageSrc =
                getVendorImageSrc(vendor);

              return (
                <article
                  className="frr-vendor-card"
                  key={vendor.vendorId}
                >
                  <button
                    type="button"
                    className="frr-vendor-image"
                    onClick={() => openVendor(vendor)}
                  >
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={vendor.vendorName}
                      />
                    ) : (
                      <span>🍽️</span>
                    )}
                  </button>

                  <div className="frr-vendor-content">
                    <div className="frr-vendor-top">
                      <div className="frr-vendor-heading">
                        <h3>{vendor.vendorName}</h3>

                        <div className="frr-rating-row">
                          {vendor.averageRating > 0 &&
                          vendor.ratingCount > 0 ? (
                            <>
                              <span className="frr-rating-stars">
                                {renderRatingStars(vendor.averageRating)}
                              </span>

                              <strong className="frr-rating-value">
                                {vendor.averageRating.toFixed(1)}
                              </strong>

                              <small>
                                ({vendor.ratingCount}{" "}
                                {vendor.ratingCount === 1
                                  ? "rating"
                                  : "ratings"})
                              </small>
                            </>
                          ) : (
                            <>
                              <span className="frr-rating-stars empty">
                                ☆☆☆☆☆
                              </span>
                              <small>No ratings yet</small>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="frr-balance-row">
                      <div className="available">
                        <span>●</span>
                        <strong>
                          {vendor.availableCoins}
                        </strong>
                        <small>Available</small>
                      </div>

                      <div className="pending">
                        <span>●</span>
                        <strong>
                          {vendor.pendingCoins}
                        </strong>
                        <small>Pending</small>
                      </div>
                    </div>

                    <div className="frr-card-actions">
                      <button
                        type="button"
                        className="frr-use-btn"
                        disabled={vendor.availableCoins <= 0}
                        onClick={() => useCoins(vendor)}
                      >
                        <span className="frr-action-icon">🪙</span>
                        Use Coins
                      </button>

                      <button
                        type="button"
                        className="frr-view-btn"
                        onClick={() => openVendor(vendor)}
                      >
                        View Shop
                        <span className="frr-view-arrow">›</span>
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </section>
        )}

        <section className="frr-how-it-works">
          <div className="frr-section-head">
            <div>
              <p>SIMPLE FLOW</p>
              <h2>How rewards work</h2>
            </div>
          </div>

          <div className="frr-flow-grid">
            <article>
              <span>1</span>
              <strong>Order</strong>
              <p>Place an order through Food Radar.</p>
            </article>

            <article>
              <span>2</span>
              <strong>Earn</strong>
              <p>Earn Shop Coins on eligible orders.</p>
            </article>

            <article>
              <span>3</span>
              <strong>Use</strong>
              <p>Use available coins on your next eligible bill.</p>
            </article>
          </div>
        </section>
      </main>

      <nav className="frr-bottom-nav">
        <button
          type="button"
          onClick={() =>
            (window.location.href = "/food-radar")
          }
        >
          <span>⌂</span>
          Home
        </button>

        <button
          type="button"
          onClick={() =>
            (window.location.href =
              "/food-radar/offers")
          }
        >
          <span>◇</span>
          Offers
        </button>

        <button type="button" className="active">
          <span>🎁</span>
          Rewards
        </button>

        <button type="button">
          <span>▣</span>
          Orders
        </button>

        <button
          type="button"
          onClick={() =>
            (window.location.href = "/food-radar")
          }
        >
          <span>♙</span>
          Profile
        </button>
      </nav>
    </div>
  );
}

export default FoodRadarRewards;
